import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import {
  conversationSnapshotToFrame,
  createConversationStreamClient,
  getConversationChatSnapshot,
  submitConversationChatCommand
} from '@/api/conversation'
import type { ConversationCommand } from '@/api/conversation'
import type { ConversationFrame } from '@/api/conversation/stream'
import { failureLine } from '@/common/errorMessages'
import { publishArtifactEvents } from './artifactChanges'
import { conversationReducer, initialConversationState } from './state'
import type { WorkspaceArtifactEventInput } from './artifactChanges'

export type ConversationSurfaceStatus = 'loading' | 'ready' | 'unavailable' | 'failure' | 'unsupported'

export type ConversationController = {
  state: ReturnType<typeof conversationReducer>
  status: ConversationSurfaceStatus
  error?: string
  olderError?: string
  retry: () => void
  reconnect: () => void
  submit: (command: ConversationCommand) => Promise<void>
  loadOlder: () => Promise<void>
}

export type ConversationControllerOptions = {
  reloadApplication?: () => void
}

type SnapshotRequest = {
  projectId: number
  conversationId: number
  generation: number
  sequence: number
  controller: AbortController
}

// Every surfaced conversation failure falls back to this line when the thrown
// value carries no usable message.
const loadFailureFallback = 'The conversation could not be loaded. Try again.'

function isUnavailable(error: unknown): boolean {
  const message = failureLine(error, loadFailureFallback).toLowerCase()
  return message.includes('not enabled') || message.includes('unavailable')
}

function defaultReloadApplication(): void {
  if (typeof window !== 'undefined') window.location.reload()
}

// A dropped stream is the normal case, not the exceptional one: proxies and server
// write deadlines end long-lived responses. Recover without the user, and only fall
// back to the manual banner once this budget is spent. Each entry is one attempt.
const autoReconnectDelaysMs = [500, 1_000, 2_000, 4_000, 8_000]

// Pure projection of one decoded frame into workspace-store events: no store access
// here, so the live-frame callback below stays the only writer and every other path
// (snapshot application, replayed history) structurally cannot publish.
export function frameToArtifactEvents(frame: ConversationFrame): Array<WorkspaceArtifactEventInput> {
  if (frame.type !== 'artifact-change') return []
  switch (frame.artifactType) {
    case 'proposal':
    case 'proposal-section':
      return frame.proposalId === undefined
        ? []
        : [
            {
              projectId: frame.projectId,
              kind: 'proposal',
              proposalId: frame.proposalId,
              ...(frame.sectionId === undefined ? {} : { sectionId: frame.sectionId })
            }
          ]
    case 'presentation':
    case 'presentation-slide':
      return frame.presentationId === undefined
        ? []
        : [
            {
              projectId: frame.projectId,
              kind: 'presentation',
              presentationId: frame.presentationId,
              ...(frame.slideId === undefined ? {} : { slideId: frame.slideId })
            }
          ]
    case 'document':
      return frame.documentId === undefined
        ? []
        : [{ projectId: frame.projectId, kind: 'document', documentId: frame.documentId }]
  }
}

export function useConversationController(
  projectId: number,
  conversationId: number,
  options: ConversationControllerOptions = {}
): ConversationController {
  const [state, dispatch] = useReducer(conversationReducer, initialConversationState)
  const [status, setStatus] = useState<ConversationSurfaceStatus>('loading')
  const [error, setError] = useState<string>()
  const [olderError, setOlderError] = useState<string>()
  const [generation, setGeneration] = useState(0)
  const [refreshSequence, setRefreshSequence] = useState(0)
  const clientRef = useRef<ReturnType<typeof createConversationStreamClient> | undefined>(undefined)
  const refreshRef = useRef(false)
  const refreshRetryRef = useRef<{ tailAfterEventId: number; attempts: number } | undefined>(undefined)
  const refreshRetryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const recoveryHaltedRef = useRef(false)
  const connectRef = useRef<(lastEventId?: number) => void>(() => undefined)
  const snapshotRequestRef = useRef<SnapshotRequest | undefined>(undefined)
  const olderRequestRef = useRef<
    | {
        projectId: number
        conversationId: number
        threadId: number
        cursor: string
        controller: AbortController
      }
    | undefined
  >(undefined)
  const sequenceRef = useRef(0)
  const scopeRef = useRef({ projectId, conversationId, generation })
  scopeRef.current = { projectId, conversationId, generation }
  const snapshotRef = useRef(state)
  snapshotRef.current = state

  const applySnapshot = useCallback(async () => {
    snapshotRequestRef.current?.controller.abort()
    const request: SnapshotRequest = {
      projectId,
      conversationId,
      generation,
      sequence: sequenceRef.current + 1,
      controller: new AbortController()
    }
    sequenceRef.current = request.sequence
    snapshotRequestRef.current = request
    let snapshot
    try {
      snapshot = await getConversationChatSnapshot(projectId, conversationId, { signal: request.controller.signal })
    } catch (snapshotError) {
      if (snapshotRequestRef.current !== request || request.controller.signal.aborted) return undefined
      throw snapshotError
    }
    if (snapshotRequestRef.current !== request || request.controller.signal.aborted) return undefined
    if (
      scopeRef.current.projectId !== request.projectId ||
      scopeRef.current.conversationId !== request.conversationId ||
      scopeRef.current.generation !== request.generation
    ) {
      return undefined
    }
    if (snapshot.threadId !== request.conversationId) {
      throw new Error('The conversation snapshot identity did not match the requested chat.')
    }
    const current = snapshotRef.current
    if (
      current.projectId === projectId &&
      current.threadId === snapshot.threadId &&
      snapshot.highWaterEventId < current.tailAfterEventId
    ) {
      return { snapshot, applied: false } as const
    }
    dispatch({
      type: 'frame-received',
      frame: conversationSnapshotToFrame(projectId, snapshot),
      receivedAtTsMs: Date.now()
    })
    return { snapshot, applied: true } as const
  }, [conversationId, generation, projectId])

  const connect = useCallback(
    (lastEventId?: number) => {
      const previousClient = clientRef.current
      clientRef.current = undefined
      previousClient?.close()
      dispatch({ type: 'connect-started' })
      function ownsConnection(): boolean {
        return (
          scopeRef.current.projectId === projectId &&
          scopeRef.current.conversationId === conversationId &&
          scopeRef.current.generation === generation &&
          clientRef.current === client
        )
      }
      const client = createConversationStreamClient({
        projectId,
        conversationId,
        onFrame: (frame) => {
          if (!ownsConnection() || frame.projectId !== projectId || frame.threadId !== conversationId) return
          // A delivered frame proves this connection works, so the next drop starts
          // from a full budget rather than inheriting the last outage's attempts.
          reconnectAttemptsRef.current = 0
          // Only a live frame counts as a fresh change: publication sits here and nowhere
          // else, so a snapshot refresh can never masquerade as one in the canvas store.
          if (frame.type === 'artifact-change') publishArtifactEvents(frameToArtifactEvents(frame))
          dispatch({ type: 'frame-received', frame, receivedAtTsMs: Date.now() })
        },
        rebootstrap: () => {
          if (!ownsConnection()) return Promise.resolve()
          recoveryHaltedRef.current = true
          ;(options.reloadApplication ?? defaultReloadApplication)()
          return Promise.resolve()
        },
        onUnsupported: (protocolError) => {
          if (!ownsConnection()) return
          recoveryHaltedRef.current = true
          setError(protocolError.message)
          setStatus('unsupported')
          dispatch({ type: 'protocol-mismatch' })
        }
      })
      clientRef.current = client
      const connectionEnded = (streamError?: unknown) => {
        if (!ownsConnection() || recoveryHaltedRef.current) return
        dispatch({ type: 'stream-disconnected' })
        const delay = autoReconnectDelaysMs[reconnectAttemptsRef.current]
        if (delay === undefined) {
          setError(
            streamError === undefined
              ? 'Live updates stopped. Reconnect to continue.'
              : failureLine(streamError, loadFailureFallback)
          )
          setStatus('ready')
          return
        }
        reconnectAttemptsRef.current += 1
        if (reconnectTimerRef.current !== undefined) clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = undefined
          const cursor = snapshotRef.current.tailAfterEventId
          connectRef.current(cursor === 0 ? undefined : cursor)
        }, delay)
      }
      // A stream that ends cleanly is as dead as one that errors: recover from both.
      void client.connect(lastEventId).then(
        () => connectionEnded(),
        (streamError: unknown) => connectionEnded(streamError)
      )
    },
    [conversationId, generation, options.reloadApplication, projectId]
  )
  connectRef.current = connect

  useEffect(() => {
    let active = true
    setStatus('loading')
    setError(undefined)
    setOlderError(undefined)
    reconnectAttemptsRef.current = 0
    recoveryHaltedRef.current = false
    applySnapshot()
      .then((result) => {
        if (!active || result === undefined || !result.applied) return
        setStatus('ready')
        connect(result.snapshot.tailAfterEventId === 0 ? undefined : result.snapshot.tailAfterEventId)
      })
      .catch((bootstrapError: unknown) => {
        if (!active) return
        setError(failureLine(bootstrapError, loadFailureFallback))
        setStatus(isUnavailable(bootstrapError) ? 'unavailable' : 'failure')
      })
    return () => {
      active = false
      if (refreshRetryTimerRef.current !== undefined) clearTimeout(refreshRetryTimerRef.current)
      refreshRetryTimerRef.current = undefined
      if (reconnectTimerRef.current !== undefined) clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = undefined
      refreshRetryRef.current = undefined
      refreshRef.current = false
      snapshotRequestRef.current?.controller.abort()
      olderRequestRef.current?.controller.abort()
      clientRef.current?.close()
      clientRef.current = undefined
    }
  }, [applySnapshot, connect, generation])

  useEffect(() => {
    if (status !== 'ready' || !state.snapshotRefreshRequired || refreshRef.current) return
    refreshRef.current = true
    void applySnapshot()
      .then((result) => {
        if (result?.applied) {
          refreshRetryRef.current = undefined
          return
        }
        const current = snapshotRef.current
        if (result === undefined || !current.snapshotRefreshRequired) return
        const target = current.tailAfterEventId
        const retry = refreshRetryRef.current
        const attempts = retry?.tailAfterEventId === target ? retry.attempts : 0
        if (attempts >= 1) {
          const currentClient = clientRef.current
          clientRef.current = undefined
          currentClient?.close()
          setError('Live updates could not catch up. Reconnect to try again.')
          setStatus('ready')
          dispatch({ type: 'stream-disconnected' })
          return
        }
        if (refreshRetryTimerRef.current !== undefined) return
        refreshRetryRef.current = { tailAfterEventId: target, attempts: attempts + 1 }
        refreshRetryTimerRef.current = setTimeout(() => {
          refreshRetryTimerRef.current = undefined
          setRefreshSequence((currentSequence) => currentSequence + 1)
        }, 25)
      })
      .catch((refreshError: unknown) => {
        setError(failureLine(refreshError, loadFailureFallback))
        dispatch({ type: 'stream-disconnected' })
      })
      .finally(() => {
        refreshRef.current = false
      })
  }, [applySnapshot, refreshSequence, state.snapshotRefreshRequired, state.tailAfterEventId, status])

  const retry = useCallback(() => setGeneration((current) => current + 1), [])
  const reconnect = useCallback(() => {
    if (refreshRetryTimerRef.current !== undefined) clearTimeout(refreshRetryTimerRef.current)
    refreshRetryTimerRef.current = undefined
    refreshRetryRef.current = undefined
    refreshRef.current = false
    if (reconnectTimerRef.current !== undefined) clearTimeout(reconnectTimerRef.current)
    reconnectTimerRef.current = undefined
    reconnectAttemptsRef.current = 0
    setStatus('ready')
    setError(undefined)
    const cursor = snapshotRef.current.tailAfterEventId
    connect(cursor === 0 ? undefined : cursor)
    if (snapshotRef.current.snapshotRefreshRequired) {
      setRefreshSequence((currentSequence) => currentSequence + 1)
    }
  }, [connect])

  const submit = useCallback(
    async (command: ConversationCommand) => {
      const submittedScope = { projectId, conversationId, generation }
      await submitConversationChatCommand(projectId, conversationId, command)
      if (
        scopeRef.current.projectId !== submittedScope.projectId ||
        scopeRef.current.conversationId !== submittedScope.conversationId ||
        scopeRef.current.generation !== submittedScope.generation
      ) {
        return
      }
      await applySnapshot()
    },
    [applySnapshot, conversationId, generation, projectId]
  )

  const loadOlder = useCallback(async () => {
    const current = snapshotRef.current
    if (!current.hasOlder || current.earliestCursor === undefined || current.threadId === undefined) return
    olderRequestRef.current?.controller.abort()
    const request = {
      projectId,
      conversationId,
      threadId: current.threadId,
      cursor: current.earliestCursor,
      controller: new AbortController()
    }
    olderRequestRef.current = request
    setOlderError(undefined)
    let older
    try {
      older = await getConversationChatSnapshot(projectId, conversationId, {
        before: request.cursor,
        limit: 100,
        signal: request.controller.signal
      })
    } catch (olderPageError) {
      if (olderRequestRef.current !== request || request.controller.signal.aborted) return
      setOlderError(failureLine(olderPageError, loadFailureFallback))
      return
    }
    const latest = snapshotRef.current
    if (
      olderRequestRef.current !== request ||
      request.controller.signal.aborted ||
      latest.projectId !== request.projectId ||
      request.conversationId !== conversationId ||
      latest.threadId !== request.threadId ||
      latest.earliestCursor !== request.cursor ||
      older.threadId !== request.threadId
    ) {
      return
    }
    dispatch({
      type: 'older-page-loaded',
      messages: older.messages,
      tasks: older.tasks,
      hasOlder: older.hasOlder,
      ...(older.earliestCursor === undefined ? {} : { earliestCursor: older.earliestCursor })
    })
  }, [conversationId, projectId])

  const ownsRequestedScope = state.projectId === projectId && state.threadId === conversationId
  const exposedState = ownsRequestedScope ? state : initialConversationState
  const exposedStatus = ownsRequestedScope || status !== 'ready' ? status : 'loading'

  return {
    state: exposedState,
    status: exposedStatus,
    ...(error === undefined ? {} : { error }),
    ...(olderError === undefined ? {} : { olderError }),
    retry,
    reconnect,
    submit,
    loadOlder
  }
}
